import { Trans, useLingui } from '@lingui/react/macro';
import { X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Dismissible "What are templates?" info banner on the templates landing page.
 * Shows a spot illustration alongside explainer copy; the dismissed state
 * persists in `localStorage` so the banner stays hidden across reloads.
 */
export function TemplatesBanner() {
  const { t } = useLingui();
  const [dismissed, setDismissed] = useState(() => readDismissed());

  if (dismissed) return null;

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // Ignore storage failures — the banner just won't persist its dismissal.
    }
  }

  return (
    <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 to-accent/30">
      <CardContent className="flex items-start gap-4 p-4">
        <TemplatesSpotIllustration />
        <div className="min-w-0 flex-1">
          <p className="text-[length:var(--body-font-size)] leading-[var(--body-line-height)] font-semibold">
            <Trans>What are templates?</Trans>
          </p>
          <p className="mt-1 text-[length:var(--body-font-size)] leading-[var(--body-line-height)] text-[var(--muted-foreground)]">
            <Trans>
              Templates are pre-configured shift patterns that can be quickly applied to your rotas.
              They define recurring shift schedules, staffing requirements, and assignment rules.
              Create your own templates or use ones shared by your team to save time when building
              rotas.
            </Trans>
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t`Dismiss`}
          className="shrink-0"
          onClick={dismiss}
        >
          <X className="size-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

const DISMISSED_KEY = 'rr_templates_banner_dismissed';

/** Reads the persisted dismissal flag, treating storage errors as "not dismissed". */
function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Decorative spot illustration — three overlapping calendar cards with coloured
 * shift blocks, echoing the rota-template motif. Purely ornamental.
 */
function TemplatesSpotIllustration() {
  return (
    <svg
      width="56"
      height="56"
      viewBox="0 0 56 56"
      fill="none"
      aria-hidden="true"
      className="hidden shrink-0 sm:block"
    >
      <rect x="6" y="14" width="30" height="34" rx="4" className="fill-card stroke-border" />
      <rect x="13" y="9" width="30" height="34" rx="4" className="fill-card stroke-border" />
      <rect x="20" y="4" width="30" height="34" rx="4" className="fill-card stroke-border" />
      <rect x="20" y="4" width="30" height="9" rx="4" className="fill-primary/80" />
      <rect x="25" y="19" width="8" height="5" rx="1.5" className="fill-shift-teal" />
      <rect x="37" y="19" width="8" height="5" rx="1.5" className="fill-shift-indigo" />
      <rect x="25" y="28" width="8" height="5" rx="1.5" className="fill-shift-violet" />
      <rect x="37" y="28" width="8" height="5" rx="1.5" className="fill-shift-sky" />
    </svg>
  );
}
